<script setup lang="ts">
import { computed, watch, onMounted, ref } from 'vue'
import type { SurveyQuestion } from '@/types/surveyTypes'
import { Field, ErrorMessage } from 'vee-validate'
import { useSurvey, AnswerTarget } from '@/composables/onlineSurvey/useSurvey'

const { setAnswer, answers } = useSurvey()

const props = defineProps<{
  question: SurveyQuestion
  answerTarget?: AnswerTarget
}>()

const selectedValue = ref<string>('')

// Helper-Funktion um die richtige Datenquelle zu bekommen
const getDataSource = () => {
  return answers
}

const handleChange = (event: Event) => {
  const target = event.target as HTMLSelectElement
  selectedValue.value = target.value
  setAnswer(props.question.id, props.question.label, target.value, props.answerTarget)
}

const handleImageClick = (optionValue: string) => {
  selectedValue.value = optionValue
  setAnswer(props.question.id, props.question.label, optionValue, props.answerTarget)
}

const isSelected = (optionValue: string) => selectedValue.value === optionValue

const fieldName = computed(() => props.question.id)

// Check if any option has an image_url
const hasImages = computed(() => {
  return props.question.options?.some((option) => option.image_url)
})

const initializeValue = () => {
  const dataSource = getDataSource()
  const existingAnswer = dataSource.value.find((a) => a.id === props.question.id)
  if (existingAnswer !== undefined) {
    selectedValue.value = existingAnswer.answer || ''
    return existingAnswer.answer
  }
  selectedValue.value = ''
  return undefined
}

onMounted(() => {
  initializeValue()
})

watch(
  () => getDataSource().value.find((a) => a.id === props.question.id),
  () => {
    initializeValue()
  },
)
</script>

<template>
  <div class="field">
    <Field :name="fieldName" :model-value="selectedValue" class="is-hidden" />

    <!-- Bildbasierte Auswahl -->
    <div v-if="hasImages" class="columns is-multiline is-centered">
      <div
        v-for="option in props.question.options"
        :key="option.value"
        class="column is-3-desktop is-6-tablet is-12-mobile is-flex is-align-items-center is-justify-content-start is-flex-direction-column"
        :class="{
          'is-selected': isSelected(option.value),
        }"
        @click="handleImageClick(option.value)"
      >
        <!-- Bild Container -->
        <div class="image-container">
          <img
            :src="option.image_url"
            :alt="option.image_alt || option.label"
            class="choice-image"
          />
          <!-- Auswahl-Indikator -->
          <div v-if="isSelected(option.value)" class="selection-indicator is-size-5">✓</div>
        </div>

        <!-- Label unter dem Bild -->
        <p class="choice-label has-text-weight-bold">{{ option.label }}</p>
      </div>
    </div>

    <!-- Fallback: Standard Dropdown -->
    <div v-else class="control">
      <div class="select is-fullwidth">
        <Field :name="fieldName" :value="initializeValue()" v-slot="{ field, errorMessage }">
          <select
            :id="props.question.id"
            v-bind="field"
            :class="{ 'is-danger': errorMessage }"
            @change="handleChange"
          >
            <option value="">
              {{ props.question.placeholder || 'Bitte wählen Sie eine Option...' }}
            </option>
            <option
              v-for="option in props.question.options"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </Field>
      </div>
    </div>

    <ErrorMessage :name="fieldName" class="help is-danger" />
  </div>
</template>

<style>
@import '../../../assets/main.css';
/* Grid Layout für Bild-Auswahl */

.choice-label {
  font-size: 1rem !important;
}

.choice-image:hover:not(.is-disabled) {
  border-color: var(--primary-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.is-selected .choice-image {
  border-color: var(--primary-color);
}

/* Auswahl-Indikator */
.selection-indicator {
  position: absolute;
  top: -5px;
  right: 10px;
  width: 36px;
  height: 36px;
  background-color: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.image-choice-item.is-selected .choice-label {
  color: var(--primary-color);
  font-weight: 600;
}

.stepper-item-question-label {
  color: var(--primary-color);
}

/* Einzelne Bild-Option */
.image-choice-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 0.75rem;
  border-radius: 0.75rem;
  border: 4px solid transparent;
  transition: all 0.3s ease;
  background-color: var(--bulma-scheme-main, #ffffff);
}

.image-choice-item.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Bild Container */
.image-container {
  position: relative;
  width: 60%;
  height: auto;
  margin-bottom: 0.25rem;
}

.choice-image {
  width: 100%;
  height: auto;
  min-height: 100px;
  border-radius: 50%;
  transition: all 0.3s ease;
  border: 5px solid var(--bulma-border, #dbdbdb);
}

/* Label unter dem Bild */
.choice-label {
  text-align: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--bulma-text, #4a4a4a);
  line-height: 1.25;
  margin: 0;
}
</style>
